/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AxiosResponse } from 'axios';
import { call, delay, put, takeLatest } from 'redux-saga/effects';

import api from '../../api/api';
import { errorHandling } from '../../api/errorHandling';
import { mapSuccessResponseToState } from '../../api/responseHandling';
import * as commonSagas from '../_common/commonSagas';
import { getApiVersion } from '../apiVersion';
import * as requestedMsgSlice from './requestedMsgSlice';

const TABLE = 'requestedMsg';

const basePath = (action: Parameters<typeof getApiVersion>[1]) =>
	commonSagas.buildTablePath(TABLE, getApiVersion(TABLE, action)).path;

// Strips the "data:<mime>;base64," prefix — the backend expects raw base64.
export const fileToBase64 = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = reader.result as string;
			resolve(result.slice(result.indexOf(',') + 1));
		};
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(file);
	});

// RequestedMsg attachments live on dedicated `files/*` endpoints (JSON with
// base64 content), not the generic `{id}/documents` FileStorage routes the
// common sagas call — so the three document sagas are replaced here.

// The list endpoint returns file metadata embedded in the message DTO
// (Files, without blobs). Blobs are fetched lazily on click through
// DownloadUrl, so listing attachments never transfers file content.
export function* getRequestedMsgFiles(props: any) {
	const { id } = props.payload;
	try {
		const path = basePath('getItemDocuments');
		const response: AxiosResponse<any> = yield call(api.get, `${path}/${id}`);
		const files = response?.data?.Files ?? [];
		const documents = files.map((f: any) => ({
			Id: f.Id,
			Name: f.Name,
			Type: f.MediaType,
			Extension: f.Name?.includes('.') ? f.Name.split('.').pop() : undefined,
			Thumbnail: null,
			DocData: null,
			DownloadUrl: `${path}/files/${f.Id}`,
		}));
		yield put(requestedMsgSlice.getItemDocumentsSuccess({ data: documents }));
		yield delay(0);
		yield put(requestedMsgSlice.getItemDocumentsFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, requestedMsgSlice.getItemDocumentsFailure);
	}
}

export function* uploadRequestedMsgFiles(props: any) {
	const { id, files } = props.payload;
	// Nothing selected: finish quietly — running on would hand a null
	// response to mapSuccessResponseToState and report a failure for a no-op.
	if (!files || !Array.from(files as FileList).length) {
		yield put(requestedMsgSlice.uploadItemDocumentsFinish());
		return;
	}
	try {
		const path = basePath('uploadItemDocuments');
		let response: AxiosResponse<any> | null = null;
		for (const file of Array.from(files as FileList)) {
			const fileData: string = yield call(fileToBase64, file);
			response = yield call(api.post, `${path}/files/create`, {
				RequestedMsgId: id,
				Name: file.name || 'noname',
				MediaType: file.type || 'application/octet-stream',
				FileData: fileData,
			});
		}
		yield put(
			requestedMsgSlice.uploadItemDocumentsSuccess(
				mapSuccessResponseToState(
					response as AxiosResponse<any>,
					'documentsUploaded',
				),
			),
		);
		yield delay(0);
		yield put(requestedMsgSlice.uploadItemDocumentsFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, requestedMsgSlice.uploadItemDocumentsFailure);
	}
}

// Create with attachments: the files picked in the create modal go inline in
// the create body (Files array of base64 payloads) and the backend inserts
// message and files in one transaction — the sending service can never pick
// up the message before its attachments exist.
export function* createRequestedMsg(props: any) {
	const { data, files } = props.payload;
	try {
		const path = basePath('createItem');
		let body = data;
		if (Array.isArray(files) && files.length) {
			const inlineFiles: Record<string, unknown>[] = [];
			for (const file of files as File[]) {
				const fileData: string = yield call(fileToBase64, file);
				inlineFiles.push({
					Name: file.name || 'noname',
					MediaType: file.type || 'application/octet-stream',
					FileData: fileData,
				});
			}
			body = { ...data, Files: inlineFiles };
		}
		const response: AxiosResponse<any> = yield call(
			api.post,
			`${path}/create`,
			body,
		);
		yield put(
			requestedMsgSlice.createItemSuccess(
				mapSuccessResponseToState(response, 'created'),
			),
		);
		yield delay(0);
		yield put(requestedMsgSlice.createItemFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, requestedMsgSlice.createItemFailure);
	}
}

export function* deleteRequestedMsgFile(props: any) {
	const { documentId } = props.payload;
	try {
		const path = basePath('deleteItemDocument');
		const response: AxiosResponse<any> = yield call(
			api.del,
			`${path}/files/delete/${documentId}`,
			{},
		);
		yield put(
			requestedMsgSlice.deleteItemDocumentSuccess(
				mapSuccessResponseToState(response, 'documentDeleted'),
			),
		);
		yield delay(0);
		yield put(requestedMsgSlice.deleteItemDocumentFinish());
	} catch (error: unknown) {
		yield* errorHandling(error, requestedMsgSlice.deleteItemDocumentFailure);
	}
}

export default function* watchRequestedMsgSaga() {
	yield takeLatest(requestedMsgSlice.getTableStart.type, commonSagas.getTable);
	yield takeLatest(requestedMsgSlice.getItemStart.type, commonSagas.getItem);
	yield takeLatest(requestedMsgSlice.createItemStart.type, createRequestedMsg);
	yield takeLatest(
		requestedMsgSlice.updateItemStart.type,
		commonSagas.updateItem,
	);
	yield takeLatest(
		requestedMsgSlice.deleteItemsStart.type,
		commonSagas.deleteItems,
	);
	yield takeLatest(
		requestedMsgSlice.deleteItemStart.type,
		commonSagas.deleteItem,
	);
	yield takeLatest(
		requestedMsgSlice.getTableStructureStart.type,
		commonSagas.getTableStructure,
	);
	yield takeLatest(
		requestedMsgSlice.getItemDocumentsStart.type,
		getRequestedMsgFiles,
	);
	yield takeLatest(
		requestedMsgSlice.uploadItemDocumentsStart.type,
		uploadRequestedMsgFiles,
	);
	yield takeLatest(
		requestedMsgSlice.deleteItemDocumentStart.type,
		deleteRequestedMsgFile,
	);
	yield takeLatest(
		requestedMsgSlice.getTableExcelStart.type,
		commonSagas.getTableExcel,
	);
}
